import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, FileText, Send, Loader2 } from "lucide-react";
import { CONTACT, DOMAINS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = { name: "", email: "", phone: "", service: "", message: "" };

const inputCls =
  "w-full bg-white border border-slate-300 focus:border-[#E31E24] focus:ring-2 focus:ring-[#E31E24]/15 outline-none px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors";

export default function ContactSection() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone: form.phone || null,
        service: form.service || null,
      });
      toast.success(t.contact.successTitle, { description: t.contact.successDesc });
      setForm(INITIAL);
    } catch {
      toast.error(t.contact.errorTitle, { description: t.contact.errorDesc });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-32 bg-slate-50 border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <div className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#E31E24] font-semibold mb-4">
              {t.contact.overline}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.contact.title}
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              {t.contact.text}
            </p>

            <div className="mt-10 space-y-5" data-testid="contact-coordinates">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 group-hover:border-[#E31E24] transition-colors">
                  <Mail size={17} className="text-[#E31E24]" />
                </span>
                <span className="text-sm font-semibold text-slate-900">{CONTACT.email}</span>
              </a>
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 group-hover:border-[#E31E24] transition-colors">
                  <Phone size={17} className="text-[#E31E24]" />
                </span>
                <span className="text-sm font-semibold text-slate-900">{CONTACT.phone}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200">
                  <MapPin size={17} className="text-[#E31E24]" />
                </span>
                <span className="text-sm font-semibold text-slate-900">{CONTACT.location}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200">
                  <FileText size={17} className="text-[#E31E24]" />
                </span>
                <span className="text-sm font-semibold text-slate-900">{CONTACT.tva}</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3 bg-white border border-slate-200 p-6 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  {t.contact.name}
                </label>
                <input
                  id="cf-name"
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.namePh}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  {t.contact.email}
                </label>
                <input
                  id="cf-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.emailPh}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  {t.contact.phone}
                </label>
                <input
                  id="cf-phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={t.contact.phonePh}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-service" className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  {t.contact.service}
                </label>
                <select
                  id="cf-service"
                  data-testid="contact-select-service"
                  value={form.service}
                  onChange={set("service")}
                  className={inputCls}
                >
                  <option value="">{t.contact.selectPh}</option>
                  {DOMAINS.map((d, i) => (
                    <option key={d.id} value={d.title}>
                      {t.expertise.domains[i]?.title || d.title}
                    </option>
                  ))}
                  <option value="Autre">{t.contact.other}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="block font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  {t.contact.message}
                </label>
                <textarea
                  id="cf-message"
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t.contact.messagePh}
                  className={`${inputCls} resize-y`}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit-button"
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#E31E24] hover:bg-[#C7181D] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={17} />
              )}
              {loading ? t.contact.sending : t.contact.submit}
            </button>
            <p className="mt-4 text-xs text-slate-400">
              {t.contact.privacy}
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
